import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, UserPlus, Gem, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * SetupWizard banner — shown at the top of the dashboard ONLY when the academy
 * is not fully configured (no classes OR no students yet). Disappears once both
 * are in place. JC sees this on first sign-in to a fresh production DB.
 */
export default function SetupWizard() {
  const [status, setStatus] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/setup_status").then(r => setStatus(r.data));
  }, []);

  if (!status || status.all_set) return null;

  const steps = [
    {
      key: "classes",
      done: status.has_classes,
      Icon: Calendar,
      title: "Add your weekly schedule",
      desc: "Set up your Gi, No-Gi, Kids, and Open Mat classes.",
      cta: "Open Classes",
      path: "/app/classes",
      testid: "setup-step-classes",
    },
    {
      key: "students",
      done: status.has_students,
      Icon: Users,
      title: "Import your roster",
      desc: "CSV import or add students one-by-one.",
      cta: "Open Students",
      path: "/app/students",
      testid: "setup-step-students",
    },
    {
      key: "team",
      done: status.has_team,
      Icon: UserPlus,
      title: "Invite your coaches",
      desc: "Give your team owner-level access.",
      cta: "Open Team",
      path: "/app/team",
      testid: "setup-step-team",
    },
    {
      key: "billing",
      done: status.has_billing,
      Icon: Gem,
      title: "Start your subscription",
      desc: "Add a card to keep your 14-day trial running past day 14.",
      cta: "Open Subscription",
      path: "/app/plan",
      testid: "setup-step-billing",
    },
  ];

  const doneCount = steps.filter(s => s.done).length;

  return (
    <div className="card p-6" data-testid="setup-wizard" style={{
      borderColor: "var(--green)",
      background: "linear-gradient(135deg, rgba(61,220,126,0.06), rgba(61,220,126,0.02))",
    }}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="label" style={{ color: "var(--green)" }}>GETTING STARTED · {doneCount}/{steps.length}</div>
          <div className="text-[18px] font-bold tracking-tight mt-1 uppercase">Set up your academy</div>
          <div className="label-tiny mt-1" style={{ color: "var(--text-dim)" }}>
            {steps.length} quick steps before your students can use the app.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map(step => (
          <button
            key={step.key}
            onClick={() => nav(step.path)}
            data-testid={step.testid}
            className="card card-hover p-5 text-left"
            style={{
              border: step.done ? "1px solid var(--green)" : "1px solid var(--border)",
              background: step.done ? "rgba(61,220,126,0.04)" : "var(--bg-card)",
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <step.Icon size={18} style={{ color: step.done ? "var(--green)" : "var(--text-dim)" }} />
              {step.done && (
                <div style={{
                  width: 22, height: 22, borderRadius: 999,
                  background: "var(--green)", color: "var(--bg)",
                  display: "grid", placeItems: "center",
                }}>
                  <Check size={13} strokeWidth={3} />
                </div>
              )}
            </div>
            <div className="text-[14px] font-bold tracking-tight uppercase" style={{
              color: step.done ? "var(--text-dim)" : "var(--text)",
              textDecoration: step.done ? "line-through" : "none",
            }}>
              {step.title}
            </div>
            <div className="label-tiny mt-1" style={{ color: "var(--text-dim)" }}>{step.desc}</div>
            {!step.done && (
              <div className="mt-3 text-[12px] font-semibold uppercase tracking-wider" style={{ color: "var(--green)" }}>
                {step.cta} <ArrowRight size={12} style={{ display: "inline", verticalAlign: "middle" }} />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
